/* eslint-disable no-console */
/* eslint-disable unicorn/no-useless-undefined */
/* eslint-disable import/no-cycle */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unicorn/consistent-function-scoping */
import LoadingButton from "@mui/lab/LoadingButton/LoadingButton";
import { CircularProgress } from "@mui/material";
import Box from "@mui/material/Box/Box";
import Divider from "@mui/material/Divider/Divider";
import Stack from "@mui/material/Stack/Stack";
import Typography from "@mui/material/Typography/Typography";
import jwtDecode from "jwt-decode";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { HttpService } from "../../../constants/HTTPService";
import Keys from "../../../constants/Keys";
import useModal from "../../../hooks/use-modal";
import { IJwtTokenData } from "../../../interfaces/IJwtTokenData";
import { IProfileApiHc } from "../../../interfaces/IProfileApiHc";
import { publicRoutes } from "../../../routes/public";
import {
  setUserAvatar,
  setUserProfile,
  setUserRole,
} from "../../../store/auth/slice";
import { loginStyle } from "../../../styles/container/login";
import {
  fetchAndDisplayProfileImage,
  GetGraphData,
} from "../../../utils/msGraph";
import {
  getMicrosoftTokenRedirect,
  loginMicrosoftRedirect,
} from "../../../utils/new-azure-ad";
import {
  getLastVisitedPage,
  setAuthToken,
  setLastVisitedPage,
} from "../../../utils/storage";
import Loading from "../../loading/loading";
import { getRoleNameProfileSA } from "./api-endpoint/callApi";

interface LoginContentProps {
  loginHint: string | null;
  redirectUri: string | null;
  cleanupAuthData: () => void;
}

export function LoginContent({
  loginHint,
  redirectUri,
  cleanupAuthData,
}: LoginContentProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showModal } = useModal();

  const [loadingActive, setLoadingActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleToken = async () => {
      try {
        const accessToken = await getMicrosoftTokenRedirect();
        if (!accessToken) return false;

        await processAuthToken(accessToken);
        return true;
      } catch (error) {
        console.error("Failed to handle redirect:", error);
        showModal({
          title: "Login Failed",
          message: "Failed to complete the login process. Please try again.",
          options: { variant: "failed" },
        });
        return false;
      }
    };

    const handleAutoLogin = async () => {
      if (!loginHint) return;

      setIsLoading(true);

      try {
        await loginMicrosoftRedirect(loginHint);
      } catch (error) {
        console.error("Auto login failed:", error);

        if (error instanceof Error) {
          if (error.name !== "BrowserAuthError") {
            showModal({
              title: "Login Failed",
              message:
                "Failed to initiate the auto login process. Please try again.",
              options: { variant: "failed" },
            });
          }
        } else {
          showModal({
            title: "Login Failed",
            message: "An unexpected error occurred. Please try again.",
            options: { variant: "failed" },
          });
        }

        setIsLoading(false);
      }
    };

    const init = async () => {
      const success = await handleToken();
      if (!success) {
        await handleAutoLogin();
      }
    };

    init();
  }, [loginHint, redirectUri]);

  const handleLogin = async () => {
    setIsLoading(true);
    const currentUrl = window.location.pathname;
    const excludedPaths = publicRoutes
      .map((route) => route.path)
      .filter((path): path is string => path !== undefined);

    if (!excludedPaths.includes(currentUrl)) {
      setLastVisitedPage(currentUrl);
    }

    try {
      await loginMicrosoftRedirect();
    } catch (error) {
      console.error("Failed to initiate login:", error);
      setIsLoading(false);
      showModal({
        title: "Login Failed",
        message: "Failed to initiate the login process. Please try again.",
        options: {
          variant: "failed",
        },
      });
    }
  };

  const processAuthToken = async (accessToken: string) => {
    try {
      setLoadingActive(true);

      // Get Graph Data
      const getAccountData: IProfileApiHc = await GetGraphData(accessToken);

      // Check if the loginHint matches the email
      if (loginHint && !getAccountData.mail.includes(loginHint)) {
        throw new Error(
          `Identity provided is not match with intended login request for "${loginHint}". Please continue with your account!`
        );
      }

      // Check if the email domain is valid
      const validDomains = ["@binus.edu"];
      const isValidEmail = validDomains.some((domain) =>
        getAccountData.mail.toLowerCase().includes(domain)
      );

      if (!isValidEmail) {
        throw new Error(
          `Invalid email address for "${getAccountData.mail}". Please use a valid "@binus.edu" email address.`
        );
      }

      // Get Token from API
      const authResponse = await HttpService.getAuthorizationTokenMsal(
        accessToken
      );

      // Decoding Token
      const { tokenData } = authResponse.data;
      const decodedToken: IJwtTokenData = jwtDecode(tokenData.token);

      // Set Storage
      setAuthToken(tokenData.token);

      // Get Profile Image and Role Profile in parallel
      const [getProfileImageBase64, getRoleProfile] = await Promise.all([
        fetchAndDisplayProfileImage(accessToken),
        getRoleNameProfileSA(decodedToken.BinusianId),
      ]);

      // Process Profile Data
      await Promise.all([
        dispatch(setUserProfile(decodedToken)),
        dispatch(setUserRole(getRoleProfile)),
        dispatch(setUserAvatar(getProfileImageBase64)),
      ]);

      // Finish Loading before navigation
      setLoadingActive(false);

      // Navigate to the last visited page or default to "/"
      const lastVisitedPage = getLastVisitedPage() || "/";
      navigate(lastVisitedPage, { replace: true });
    } catch (error) {
      console.log("Error fetching data:", error);
      setLoadingActive(false);
      cleanupAuthData();

      const currentUrl = window.location.pathname;
      window.history.replaceState({}, document.title, currentUrl);

      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred during login, please try again.";

      showModal({
        title: "Login Failed",
        message: errorMessage,
        options: {
          variant: "failed",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack component="section" sx={loginStyle.newContentStack}>
      <Box sx={loginStyle.newContainerForm}>
        <Box sx={loginStyle.contentStackBox}>
          <Typography sx={loginStyle.contentStackBoxTypography1}>
            WELCOME to
          </Typography>
          <Typography sx={loginStyle.contentStackBoxTypography1}>
            ALESA
          </Typography>
          <Typography sx={loginStyle.contentStackBoxTypography2}>
            (Application Legal Support Agreement)
          </Typography>
          <Divider sx={loginStyle.contentStackBoxDivider} />
          <Stack
            direction="column"
            gap="10px"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <LoadingButton
              variant="contained"
              color="info"
              loadingIndicator={
                <CircularProgress sx={{ color: "#5E5E5E" }} size="20px" />
              }
              sx={loginStyle.contentStackBoxLoginButton}
              onClick={handleLogin}
              loading={isLoading || loadingActive}
              disabled={isLoading || loadingActive}
            >
              <Box
                component="img"
                src="/assets/logo/microsoft.png"
                sx={
                  isLoading || loadingActive
                    ? { display: "none" }
                    : { display: "block" }
                }
              />
              <Typography
                fontFamily="Segoe UI"
                fontSize={{ xs: 15, xl: 20 }}
                fontWeight={600}
                sx={
                  isLoading || loadingActive
                    ? { display: "none" }
                    : { display: "block" }
                }
              >
                Sign in with Microsoft
              </Typography>
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
      <Box sx={loginStyle.newContainerImage}>
        <Box
          component="img"
          src={`${Keys.Public_CDN}alesa-public-cdn/get-icon-logo-byurl?uri=https://publiccdn.sharepointonline.com/binusianorg.sharepoint.com/sites/ALESA-UAT/CDNASSETS/IconLogo/alesa-login-page.png`}
          style={{ width: "100%" }}
        />
      </Box>
      <Loading open={loadingActive} />
    </Stack>
  );
}
